import { List, Map, Set } from "immutable";
import { aggregateID } from "lib/id";

import * as actions from "state/school_money/actions";
import { STATE_HYDRATE } from "state/persist";
import BasketItem from "state/school_money/entities/BasketItem";
import Checkout from "state/school_money/entities/Checkout";
import SecureTrading from "state/school_money/entities/SecureTrading";

const initialState = Map({

  isGuideOpen: false,
  guideShownDate: null,

  secureTrading: SecureTrading(),

  parents: Map(),
  isParentsLoading: false,

  schools: Map(),
  isSchoolsLoading: true,

  students: Map(),
  isStudentsLoading: true,
  lastStudentID: undefined,

  basketItems: Map(),
  expandedSchoolBasketID: null,
  basketItemsIssues: null,
  isBasketItemsIssuesModalOpen: false,

  checkout: Checkout(),

  isActivePaymentsLoading: true,
  clubBookings: Map(),
  dinnerBookings: Map(),
  otherPayments: Map(),

  expandedClubBookingID: null,
  seenClubBookingIDs: Set(),
  goToClubBookingID: null,

  isAllergiesWarningEnabled: true,
  expandedDinnerBookingID: null,
  seenDinnerBookingIDs: Set(),
  goToDinnerBookingID: null,

  expandedPaymentID: null,
  seenOtherPaymentIDs: Set(),
  goToPaymentID: null,

  shopItems: Map(),
  isShopLoading: true,
  seenShopItemIDs: Set(),
  goToShopItemID: null,

  history: Map(),
  isHistoryLoading: true,

  parentalConsentItems: Map(),
  pendingParentalConsentItems: Map(),

  shopItemSearchQuery: ""

});

function mergeHydratedState(state, action) {
  const hydratedState = action.payload.state.get("school_money");
  return state
    .set("guideShownDate", hydratedState.get("guideShownDate", null))
    .set("basketItems", hydratedState.get("basketItems", Map()).map(ofType => ofType.map(BasketItem)))
    .set("seenClubBookingIDs", hydratedState.get("seenClubBookingIDs", List()).toSet())
    .set("seenDinnerBookingIDs", hydratedState.get("seenDinnerBookingIDs", List()).toSet())
    .set("seenOtherPaymentIDs", hydratedState.get("seenOtherPaymentIDs", List()).toSet())
    .set("seenShopItemIDs", hydratedState.get("seenShopItemIDs", List()).toSet());
}

export default function reducer(state = initialState, action) {

  switch (action.type) {
    case STATE_HYDRATE:
      return mergeHydratedState(state, action);

    // secure trading: payment provider

    case actions.SECURE_TRADING_PROCESS_TOKEN:
      return state.setIn(["checkout", "status"], Checkout.status.PROCESSING);

    case actions.SECURE_TRADING_PROCESS_TOKEN_COMPLETED:
      return state
        .setIn(["checkout", "status"], Checkout.status.PENDING)
        .set("secureTrading", action.payload.secureTrading);

    case actions.SECURE_TRADING_THREEDS_STATUS_UPDATE:
      return state
        .setIn(["secureTrading", "threeds_status"], action.payload.status)
        .setIn(["checkout", "status"], Checkout.status.PROCESSING);

    case actions.SECURE_TRADING_RESET_COMPLETED:
      return state.set("secureTrading", action.payload.secureTrading);

    case actions.SECURE_TRADING_GET_JWT_STRING:
      return state
        .setIn(["checkout", "status"], Checkout.status.PREPARING)
        .setIn(["secureTrading", "card_holder_name"], action.payload.cardHolderName);

    case actions.SECURE_TRADING_GET_JWT_STRING_COMPLETED:
      return state
        .setIn(["secureTrading", "payment_request_jwt_string"], action.payload.jwtString)
        .setIn(["checkout", "status"], Checkout.status.PENDING);

    case actions.SECURE_TRADING_PROCESS_PAYMENT_RESPONSE:
      return state
        .setIn(["secureTrading", "error_code"], action.payload.paymentResult.errorcode)
        .setIn(["secureTrading", "error_message"], action.payload.paymentResult.errormessage)
        .setIn(["secureTrading", "payment_response_jwt_string"], action.payload.paymentResult.jwt)
        .setIn(["checkout", "status"], Checkout.status.PROCESSING)
      ;

    case actions.CLEAR_SECURE_TRADING_WALLET_PAY_JWT_STRING:
      return state
        .setIn(["secureTrading", "wallet_pay_jwt_string"], undefined);

    // parents

    case actions.PARENTS_FETCH:
    case actions.PARENT_EMAIL_ADDRESS_UPDATE:
      return state.set("isParentsLoading", true);

    case actions.PARENTS_FETCH_COMPLETED:
      return state
        .set("parents", action.payload.parents)
        .set("isParentsLoading", false);

    // schools

    case actions.SCHOOLS_FETCH:
      return state.set("isSchoolsLoading", true);

    case actions.SCHOOLS_FETCH_COMPLETED:
      return state
        .set("schools", action.payload.schools)
        .set("isSchoolsLoading", false);

    // students

    case actions.STUDENTS_FETCH:
      return state.set("isStudentsLoading", true);

    case actions.STUDENTS_FETCH_COMPLETED:
      return state
        .set("students", action.payload.students)
        .set("isStudentsLoading", false);

    case actions.SET_LAST_STUDENT:
      return state.set("lastStudentID", action.payload.studentID);

    // all payments

    case actions.ACTIVE_PAYMENTS_FETCH:
      return state.set("isActivePaymentsLoading", true);

    case actions.ACTIVE_PAYMENTS_FETCH_COMPLETED:
      return state
        .set("clubBookings", action.payload.clubPayments)
        .set("dinnerBookings", action.payload.dinnerPayments)
        .set("otherPayments", action.payload.otherPayments)
        .set("parentalConsentItems", action.payload.parentalConsentItems)
        .set("isActivePaymentsLoading", false);

    // basket

    case actions.BASKET_ITEM_SET:
      return state.setIn(["basketItems", action.payload.type, action.payload.basketItemID], BasketItem.fromAction(action));

    case actions.BASKET_ITEM_DELETE:
      return state.deleteIn(["basketItems", action.payload.type, action.payload.basketItemID]);

    case actions.BASKET_ITEM_DELETE_GROUP:
      return state.updateIn(["basketItems", action.payload.type], basketItems => {
        return basketItems.filterNot(basketItem => basketItem.get("groupID") === action.payload.groupID);
      });

    case actions.BASKET_ITEMS_ISSUES_SET:
      return state.set("basketItemsIssues", action.payload.issues);

    case actions.BASKET_ITEMS_ISSUES_MODAL_OPEN:
      return state
        .set("isBasketIssuesModalOpen", true)
        .set("basketItemsIssues", action.payload.issues);

    case actions.BASKET_ITEMS_ISSUES_MODAL_CLOSE:
      return state
        .set("isBasketIssuesModalOpen", false)
        .update("basketItemsIssues", issues => issues.clear());

    case actions.SET_EXPANDED_SCHOOL_BASKET:
      return state.set("expandedSchoolBasketID", action.payload.schoolID);

    // clubs

    case actions.CLUB_BOOKING_SESSION_SELECT:
      return state
        .setIn(["clubBookings", action.payload.clubBookingID, "selectedSessionIDs", action.payload.weekdayIndex], action.payload.sessionID);

    case actions.EXPANDED_CLUB_BOOKING_SET:
      return state.set("expandedClubBookingID", action.payload.clubBookingID);

    case actions.CLUB_BOOKINGS_MARK_AS_SEEN:
      return state.mergeIn(["seenClubBookingIDs"], action.payload.clubBookingIDs);

    case actions.CLUB_BOOKING_GO_TO:
      return state.set("goToClubBookingID", action.payload.clubBookingID);

    case actions.CLUB_BOOKING_GO_TO_CLEAR:
      return state.set("goToClubBookingID", null);

    // dinners

    case actions.DINNER_BOOKING_OPTION_SELECT:
      return state
        .setIn(["dinnerBookings", action.payload.dinnerBookingID, "selectedOptionIDs", action.payload.weekdayIndex], action.payload.optionID);

    case actions.SET_ALLERGIES_WARNING_STATUS:
      return state.set("isAllergiesWarningEnabled", action.payload.isEnabled);

    case actions.SET_EXPANDED_DINNER_BOOKING:
      return state.set("expandedDinnerBookingID", action.payload.dinnerBookingID);

    case actions.DINNER_BOOKINGS_MARK_AS_SEEN:
      return state.mergeIn(["seenDinnerBookingIDs"], action.payload.dinnerBookingIDs);

    case actions.DINNER_BOOKING_GO_TO:
      return state.set("goToDinnerBookingID", action.payload.dinnerBookingID);

    case actions.DINNER_BOOKING_GO_TO_CLEAR:
      return state.set("goToDinnerBookingID", null);

    // shop

    case actions.SHOP_ITEMS_FETCH_COMPLETED:
      return state
        .set("shopItems", action.payload.shopItems)
        .set("isShopLoading", false);

    case actions.SHOP_ITEMS_MARK_AS_SEEN:
      return state.mergeIn(["seenShopItemIDs"], action.payload.shopItemIDs);

    case actions.SHOP_ITEM_GO_TO:
      return state.set("goToShopItemID", aggregateID(action.payload.studentID, action.payload.shopItemID));

    case actions.SHOP_ITEM_GO_TO_CLEAR:
      return state.set("goToShopItemID", null);

    case actions.SHOP_ITEM_SET_SEARCH_QUERY:
      return state.set("shopItemSearchQuery", action.payload.searchQuery);

    // other payments

    case actions.SET_PAYMENT_INSTALLMENT_SELECTED_PRICE:
      return state.setIn(["otherPayments", action.payload.paymentID, "installments", action.payload.installmentNumber, "selectedPrice"], action.payload.selectedPrice);

    case actions.SET_EXPANDED_PAYMENT:
      return state.set("expandedPaymentID", action.payload.paymentID);

    case actions.PAYMENTS_MARK_AS_SEEN:
      return state.mergeIn(["seenOtherPaymentIDs"], action.payload.paymentIDs);

    case actions.PAYMENT_GO_TO:
      return state.set("goToPaymentID", action.payload.paymentID);

    case actions.PAYMENT_GO_TO_CLEAR:
      return state.set("goToPaymentID", null);

    // history

    case actions.HISTORY_FETCH_COMPLETED:
      return state
        .set("history", action.payload.history)
        .set("isHistoryLoading", false);

    // parental consent

    case actions.PARENTAL_CONSENT_UPDATE:
      return state.setIn(["pendingParentalConsentItems", action.payload.itemID], "saving");

    case actions.PARENTAL_CONSENT_UPDATE_SUCCEEDED:
      return state.setIn(["pendingParentalConsentItems", action.payload.itemID], "saved");

    case actions.PARENTAL_CONSENT_UPDATE_FAILED:
      return state.setIn(["pendingParentalConsentItems", action.payload.itemID], "failed");

    case actions.PENDING_PARENTAL_CONSENT_ITEMS_CLEAR_FAILURES:
      return state.update("pendingParentalConsentItems", items => items.filterNot(status => status === "failed"));

    case actions.PENDING_PARENTAL_CONSENT_ITEMS_CLEAR_SAVED:
      return state.update("pendingParentalConsentItems", items => items.filterNot(status => status === "saved"));

    // checkout

    case actions.CHECKOUT_START:
      return state
        .setIn(["checkout", "schoolID"], action.payload.schoolID)
        .setIn(["checkout", "status"], Checkout.status.PREPARING);

    case actions.CHECKOUT_TRANSACTION_DETAILS_SET:
      return state
        .setIn(["checkout", "transactionID"], action.payload.transactionID)
        .setIn(["checkout", "totalToPay"], action.payload.totalToPay)
        .setIn(["checkout", "status"], Checkout.status.PENDING)
        .setIn(["secureTrading", "wallet_pay_jwt_string"], action.payload.walletPayJWTString);

    case actions.CHECKOUT_TRANSACTION_PROCESS:
      return state.setIn(["checkout", "status"], Checkout.status.PROCESSING);

    case actions.CHECKOUT_COMPLETE:
      return state
        .setIn(["checkout", "status"], Checkout.status.COMPLETED);

    case actions.CHECKOUT_CLOSE:
      // reset checkout to initial
      return state
        .set("checkout", Checkout())
        .set("secureTrading", SecureTrading());

    case actions.CHECKOUT_ERROR_SET:
      return state
        .setIn(["checkout", "error"], action.payload.error)
        .setIn(["checkout", "errorCode"], action.payload.errorCode);

    case actions.CHECKOUT_ERROR_CLEAR:
      return state
        .setIn(["checkout", "error"], null)
        .setIn(["checkout", "errorCode"], null)
        .setIn(["checkout", "status"], Checkout.status.PENDING);

    case actions.CHECKOUT_RESTART:
      return state
        .setIn(["checkout", "error"], null)
        .setIn(["checkout", "errorCode"], null)
        .setIn(["checkout", "status"], Checkout.status.PENDING)
        .setIn(["secureTrading", "payment_request_jwt_string"], undefined)
        ;

    // guide

    case actions.GUIDE_OPEN:
      return state.set("isGuideOpen", true);

    case actions.GUIDE_CLOSE:
      return state
        .set("isGuideOpen", false)
        .set("guideShownDate", action.payload.timestamp);

    // default

    default:
      return state;

  }

}



// WEBPACK FOOTER //
// ./src/state/school_money/reducer.js